/**
 * ObiHub Faturalar — Manuel fatura oluştur, Paraşüt'te resmileştir.
 * Liste:  /panel/satislar/fatura-olustur
 * Form:   /panel/satislar/fatura-olustur/yeni
 *         /panel/satislar/fatura-olustur/:id
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { cn, formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import {
  Search, Plus, ChevronLeft, ChevronRight,
  Loader2, X, Trash2, Send, ArrowLeft, Save,
  ReceiptText, CheckCircle2, Clock3, Ban,
  FileCheck2, AlertCircle, Banknote, TrendingUp,
  Wallet, CreditCard as CreditCardIcon,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

// ── Sabitler ─────────────────────────────────────────────────────────────────

const VAT_OPTIONS  = [0, 1, 8, 10, 18, 20];

const PAYMENT_METHODS_LIST = [
  { v: "nakit",       label: "Nakit"         },
  { v: "havale",      label: "Havale / EFT"  },
  { v: "kredi_karti", label: "Kredi Kartı"   },
  { v: "cek",         label: "Çek"           },
  { v: "diger",       label: "Diğer"         },
];
const CURRENCIES   = ["TRY", "USD", "EUR", "GBP"];
const DUE_PRESETS  = [
  { label: "Aynı Gün", d: 0 },
  { label: "7 Gün",   d: 7 },
  { label: "15 Gün",  d: 15 },
  { label: "30 Gün",  d: 30 },
  { label: "60 Gün",  d: 60 },
];

const STATUS_META = {
  taslak:     { label: "Taslak",      icon: Clock3,       cls: "bg-amber-50 text-amber-700 border-amber-200"       },
  gonderildi: { label: "Gönderildi",  icon: Send,         cls: "bg-blue-50 text-blue-700 border-blue-200"          },
  odendi:     { label: "Ödendi",      icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  gecikti:    { label: "Gecikti",     icon: AlertCircle,  cls: "bg-rose-50 text-rose-700 border-rose-200"          },
  resmilesti: { label: "Resmileşti",  icon: FileCheck2,   cls: "bg-teal-50 text-teal-700 border-teal-200"          },
  iptal:      { label: "İptal",       icon: Ban,          cls: "bg-gray-100 text-gray-500 border-gray-200"         },
};

// ── Yardımcılar ───────────────────────────────────────────────────────────────

function today() { return new Date().toISOString().slice(0, 10); }

function addDays(base, n) {
  const d = new Date(base || today());
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return s;
  const M = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
  return `${String(d.getDate()).padStart(2,"0")} ${M[d.getMonth()]} ${d.getFullYear()}`;
}

function newLine() {
  return {
    _k: Math.random().toString(36).slice(2),
    product_id: null, product_name: "",
    description: "", quantity: 1,
    unit_price: 0, vat_rate: 20,
    discount_percent: 0, line_total: 0,
  };
}

function calcLine(l) {
  const base  = Number(l.quantity || 0) * Number(l.unit_price || 0);
  const after = base * (1 - Number(l.discount_percent || 0) / 100);
  return { ...l, line_total: Math.round(after * 100) / 100 };
}

function calcTotals(lines) {
  const sub = lines.reduce((s, l) => s + (l.line_total || 0), 0);
  const vat = lines.reduce((s, l) => s + (l.line_total || 0) * (Number(l.vat_rate) / 100), 0);
  return {
    subtotal:   Math.round(sub         * 100) / 100,
    vat_amount: Math.round(vat         * 100) / 100,
    total:      Math.round((sub + vat) * 100) / 100,
  };
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.taslak;
  const Icon = m.icon;
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-md text-xs font-medium border", m.cls)}>
      <Icon className="h-3 w-3 shrink-0" />
      {m.label}
    </span>
  );
}

// ── Müşteri arama ─────────────────────────────────────────────────────────────

function CustomerSearch({ value, onSelect, disabled }) {
  const [q, setQ]       = useState(value || "");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setRows([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/customers?search=${encodeURIComponent(q)}&per_page=8`);
        setRows(r.customers || r.items || []);
      } catch { setRows([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div ref={ref} className="relative">
      <Input
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Müşteri ara veya adını yaz..."
        disabled={disabled}
        className="h-9 text-sm"
      />
      {open && q.trim() && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-xl shadow-lg overflow-hidden max-h-56 overflow-y-auto">
          {rows.length === 0 && (
            <button
              className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted transition-colors"
              onMouseDown={() => {
                onSelect({ customer_id: null, customer_name: q, customer_tax_no: "", customer_tax_office: "", customer_address: "" });
                setOpen(false);
              }}
            >
              <span className="text-muted-foreground text-xs">Kayıt bulunamadı — </span>
              <span className="font-medium">"{q}" ile devam et</span>
            </button>
          )}
          {rows.map(c => (
            <button
              key={c.id}
              className="w-full text-left px-3 py-2.5 hover:bg-muted transition-colors border-b border-border last:border-0"
              onMouseDown={() => {
                onSelect({ customer_id: c.id, customer_name: c.name, customer_tax_no: c.tax_no || "", customer_tax_office: c.tax_office || "", customer_address: c.address || "" });
                setQ(c.name);
                setOpen(false);
              }}
            >
              <div className="text-sm font-medium text-foreground">{c.name}</div>
              {(c.tax_no || c.phone) && (
                <div className="text-xs text-muted-foreground mt-0.5">{[c.tax_no && `VKN: ${c.tax_no}`, c.phone].filter(Boolean).join("  ·  ")}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Ürün arama ────────────────────────────────────────────────────────────────

function ProductSearch({ value, onSelect, disabled }) {
  const [q, setQ]       = useState(value || "");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setRows([]); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get(`/products?search=${encodeURIComponent(q)}&per_page=8`);
        setRows(r.products || r.items || []);
      } catch { setRows([]); }
      finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div ref={ref} className="relative w-full">
      <Input
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Ürün / hizmet ara..."
        disabled={disabled}
        className="h-9 text-sm"
      />
      {open && q.trim() && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-xl shadow-lg overflow-hidden max-h-52 overflow-y-auto">
          {busy && (
            <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> Aranıyor…
            </div>
          )}
          {!busy && rows.length === 0 && (
            <button
              className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted transition-colors"
              onMouseDown={() => { onSelect({ product_id: null, product_name: q, unit_price: 0 }); setOpen(false); }}
            >
              <span className="text-muted-foreground text-xs">Katalogda yok — </span>
              <span className="font-medium text-primary">"{q}" olarak ekle</span>
              <span className="text-muted-foreground text-xs"> (Paraşüt'e kaydedilecek)</span>
            </button>
          )}
          {rows.map(p => (
            <button
              key={p.id}
              className="w-full text-left px-3 py-2.5 hover:bg-muted transition-colors border-b border-border last:border-0 flex items-center justify-between"
              onMouseDown={() => {
                onSelect({ product_id: p.id, product_name: p.name, unit_price: p.price || p.selling_price || 0 });
                setQ(p.name);
                setOpen(false);
              }}
            >
              <span className="text-sm font-medium text-foreground">{p.name}</span>
              {(p.price || p.selling_price) && (
                <span className="text-xs text-muted-foreground tabular-nums ml-2">{formatCurrency(p.price || p.selling_price)}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Ödeme Modalı ─────────────────────────────────────────────────────────────

function PaymentModal({ invoiceId, remaining, onClose, onSuccess }) {
  const [amount, setAmount]   = useState(remaining > 0 ? String(remaining) : "");
  const [date, setDate]       = useState(today());
  const [method, setMethod]   = useState("nakit");
  const [notes, setNotes]     = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/finance/accounts").then((res) => {
      if (!active) return;
      const rows = Array.isArray(res?.items) ? res.items : [];
      setAccounts(rows);
      setAccountId(rows[0]?.id || "");
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Geçerli bir tutar girin"); return; }
    setSaving(true);
    try {
      await api.post(`/local-invoices/${invoiceId}/payment`, { amount: amt, date, method, notes, account_id: accountId || null });
      toast.success("Ödeme kaydedildi");
      onSuccess();
    } catch (err) { toast.error(err?.message || "Ödeme kaydedilemedi"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm surface-card-soft p-6 space-y-4 z-10">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-foreground">Ödeme Ekle</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Tutar (₺) *</label>
            <input
              type="number" min="0.01" step="0.01" required
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0,00"
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring text-right tabular-nums"
            />
            {remaining > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Kalan bakiye: <span className="font-semibold text-foreground tabular-nums">{formatCurrency(remaining)}</span>
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Ödeme Tarihi</label>
            <input
              type="date" required
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Ödeme Yöntemi</label>
            <select
              value={method} onChange={e => setMethod(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {PAYMENT_METHODS_LIST.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Tahsilat Hesabı</label>
            <select
              value={accountId} onChange={e => setAccountId(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name} · {formatCurrency(a.balance || 0, a.currency || "TRY")}</option>)}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">Tahsilat seçilen kasa veya banka hesabına otomatik işlenir.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Not (opsiyonel)</label>
            <input
              type="text" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Dekont no, açıklama…"
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose} className="flex-1">İptal</Button>
            <Button type="submit" size="sm" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Banknote className="h-3.5 w-3.5" />}
              Kaydet
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Form Sayfası
// ══════════════════════════════════════════════════════════════════════════════

export function ObiHubInvoiceFormPage() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const isEdit    = Boolean(id && id !== "yeni");

  const [loading, setLoading]       = useState(isEdit);
  const [saving, setSaving]         = useState(false);
  const [resmi, setResmi]           = useState(false);
  const [savedId, setSavedId]       = useState(isEdit ? id : null);
  const [payments, setPayments]     = useState([]);
  const [paidAmount, setPaidAmount] = useState(0);
  const [remaining, setRemaining]   = useState(0);
  const [showPay, setShowPay]       = useState(false);

  const [form, setForm] = useState({
    customer_id: null, customer_name: "",
    customer_tax_no: "", customer_tax_office: "", customer_address: "",
    issue_date: today(), due_date: today(),
    currency: "TRY", notes: "",
    lines: [newLine()],
    _status: null, _invoice_number: null,
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const inv = await api.get(`/local-invoices/${id}`);
        setForm({
          customer_id:         inv.customer_id || null,
          customer_name:       inv.customer_name || "",
          customer_tax_no:     inv.customer_tax_no || "",
          customer_tax_office: inv.customer_tax_office || "",
          customer_address:    inv.customer_address || "",
          issue_date:          inv.issue_date || today(),
          due_date:            inv.due_date || today(),
          currency:            inv.currency || "TRY",
          notes:               inv.notes || "",
          lines: (inv.items || [newLine()]).map(i => ({ ...i, _k: Math.random().toString(36).slice(2) })),
          _status:         inv.status,
          _invoice_number: inv.invoice_number,
        });
        setPayments(inv.payments || []);
        setPaidAmount(inv.paid_amount || 0);
        setRemaining(inv.remaining_amount ?? inv.total ?? 0);
      } catch {
        toast.error("Fatura yüklenemedi");
        navigate("/panel/satislar/fatura-olustur");
      } finally { setLoading(false); }
    })();
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const setLine = (idx, patch) =>
    setForm(f => ({ ...f, lines: f.lines.map((l, i) => i === idx ? calcLine({ ...l, ...patch }) : l) }));

  const addLine    = () => setForm(f => ({ ...f, lines: [...f.lines, newLine()] }));
  const removeLine = idx => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const totals      = calcTotals(form.lines);
  const invoiceTotal = totals.total;
  const readonly = form._status === "resmilesti" || form._status === "iptal";

  const buildPayload = () => ({
    customer_id: form.customer_id,
    customer_name: form.customer_name,
    customer_tax_no: form.customer_tax_no,
    customer_tax_office: form.customer_tax_office,
    customer_address: form.customer_address,
    issue_date: form.issue_date,
    due_date: form.due_date,
    currency: form.currency,
    notes: form.notes,
    invoice_type: "satis",
    items: form.lines.map(({ _k, ...rest }) => rest),
    ...totals,
  });

  const handleSave = async () => {
    if (!form.customer_name.trim()) { toast.error("Müşteri adı zorunlu"); return; }
    setSaving(true);
    try {
      if (savedId) {
        await api.put(`/local-invoices/${savedId}`, buildPayload());
        toast.success("Fatura güncellendi");
      } else {
        const result = await api.post("/local-invoices", buildPayload());
        setSavedId(result.id);
        setForm(f => ({ ...f, _status: "taslak", _invoice_number: result.invoice_number }));
        window.history.replaceState({}, "", `/panel/satislar/fatura-olustur/${result.id}`);
        toast.success("Fatura kaydedildi");
      }
    } catch (e) { toast.error(e?.message || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  const refreshPayments = useCallback(async () => {
    if (!savedId) return;
    try {
      const inv = await api.get(`/local-invoices/${savedId}`);
      setPayments(inv.payments || []);
      setPaidAmount(inv.paid_amount || 0);
      setRemaining(inv.remaining_amount ?? inv.total ?? 0);
      setForm(f => ({ ...f, _status: inv.status }));
    } catch { /* swallow */ }
  }, [savedId]);

  const handleResmilestir = async () => {
    if (!savedId) { toast.error("Önce kaydedin"); return; }
    setResmi(true);
    try {
      await api.post(`/local-invoices/${savedId}/resmilestir`);
      toast.success("Fatura Paraşüt'e gönderildi ve resmileştirildi ✓");
      navigate("/panel/satislar/fatura-olustur");
    } catch (e) { toast.error(e?.message || "Resmileştirme başarısız"); }
    finally { setResmi(false); }
  };

  if (loading) return (
    <div className="page-shell flex items-center justify-center min-h-[300px]">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="page-shell space-y-5">

      {/* Başlık */}
      <div className="page-header">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate("/panel/satislar/fatura-olustur")}
            className="h-8 w-8 rounded-lg border border-border bg-card hover:bg-muted/50 inline-flex items-center justify-center transition-colors shrink-0"
          >
            <ArrowLeft className="h-4 w-4 text-muted-foreground" />
          </button>
          <div className="app-page-header min-w-0">
            <h1 className="app-page-title truncate">
              {form._invoice_number || (isEdit ? "Fatura" : "Yeni Fatura")}
            </h1>
            {form._status && <div className="mt-1"><StatusBadge status={form._status} /></div>}
          </div>
        </div>

        {!readonly && (
          <div className="page-header-actions">
            <Button variant="outline" size="sm" onClick={handleSave} disabled={saving || resmi} className="gap-1.5">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Kaydet
            </Button>
            {savedId && remaining > 0 && (
              <Button size="sm" variant="outline" onClick={() => setShowPay(true)} className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                <Banknote className="h-4 w-4" />
                Ödeme Al
              </Button>
            )}
            {savedId && (
              <Button
                size="sm"
                onClick={handleResmilestir}
                disabled={saving || resmi}
                className="gap-1.5 bg-[#1487a3] hover:bg-[#117690] text-white"
              >
                {resmi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Resmileştir
              </Button>
            )}
          </div>
        )}
        {readonly && (
          <span className="text-sm text-muted-foreground">Bu fatura artık düzenlenemez.</span>
        )}
      </div>

      {/* Müşteri + Fatura detayları */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        <Card className="surface-card-soft">
          <CardContent className="pt-5 space-y-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Müşteri Bilgileri</p>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Müşteri *</label>
              <CustomerSearch
                value={form.customer_name}
                disabled={readonly}
                onSelect={c => setForm(f => ({
                  ...f,
                  customer_id:         c.customer_id,
                  customer_name:       c.customer_name,
                  customer_tax_no:     c.customer_tax_no || f.customer_tax_no,
                  customer_tax_office: c.customer_tax_office || f.customer_tax_office,
                  customer_address:    c.customer_address || f.customer_address,
                }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Vergi No (VKN / TC)</label>
                <Input value={form.customer_tax_no} onChange={e => set("customer_tax_no", e.target.value)} placeholder="0000000000" disabled={readonly} className="h-9 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Vergi Dairesi</label>
                <Input value={form.customer_tax_office} onChange={e => set("customer_tax_office", e.target.value)} placeholder="Vergi dairesi" disabled={readonly} className="h-9 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Adres</label>
              <Input value={form.customer_address} onChange={e => set("customer_address", e.target.value)} placeholder="Fatura adresi" disabled={readonly} className="h-9 text-sm" />
            </div>
          </CardContent>
        </Card>

        <Card className="surface-card-soft">
          <CardContent className="pt-5 space-y-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Fatura Detayları</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Düzenleme Tarihi</label>
                <Input type="date" value={form.issue_date} onChange={e => set("issue_date", e.target.value)} disabled={readonly} className="h-9 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Vade Tarihi</label>
                <Input type="date" value={form.due_date} onChange={e => set("due_date", e.target.value)} disabled={readonly} className="h-9 text-sm" />
              </div>
            </div>
            {!readonly && (
              <div className="flex flex-wrap gap-1.5">
                {DUE_PRESETS.map(({ label, d }) => {
                  const target = addDays(form.issue_date, d);
                  return (
                    <button key={d} type="button" onClick={() => set("due_date", target)}
                      className={cn(
                        "px-2.5 py-1 text-xs rounded-md border font-medium transition-colors",
                        form.due_date === target
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-muted-foreground border-input hover:border-foreground/40 hover:text-foreground"
                      )}
                    >{label}</button>
                  );
                })}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Para Birimi</label>
              <div className="flex gap-2">
                {CURRENCIES.map(c => (
                  <button key={c} type="button" onClick={() => !readonly && set("currency", c)}
                    className={cn(
                      "px-3 py-1.5 text-sm rounded-md border font-medium transition-colors",
                      form.currency === c
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-input hover:border-foreground/40 hover:text-foreground"
                    )}
                  >{c}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Fatura Notu</label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={e => set("notes", e.target.value)}
                disabled={readonly}
                placeholder="Müşteriye gösterilecek not…"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Kalem tablosu */}
      <Card className="surface-card-soft overflow-hidden">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Ürün / Hizmet Kalemleri</h3>
            {!readonly && (
              <button type="button" onClick={addLine}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-colors">
                <Plus className="h-4 w-4" /> Satır Ekle
              </button>
            )}
          </div>

          <div className="grid px-5 py-2.5 bg-muted/30 border-b border-border text-[11px] font-semibold text-muted-foreground uppercase tracking-wider"
            style={{ gridTemplateColumns: "1fr 72px 120px 80px 110px 32px" }}>
            <span>Ürün / Hizmet</span>
            <span className="text-right">Miktar</span>
            <span className="text-right">Br. Fiyat</span>
            <span className="text-right">KDV %</span>
            <span className="text-right">Toplam</span>
            <span />
          </div>

          <div className="divide-y divide-border">
            {form.lines.map((line, idx) => (
              <div key={line._k} className="grid px-5 py-3 items-start gap-2"
                style={{ gridTemplateColumns: "1fr 72px 120px 80px 110px 32px" }}>

                <div>
                  <ProductSearch value={line.product_name} disabled={readonly}
                    onSelect={p => setLine(idx, { product_id: p.product_id, product_name: p.product_name, unit_price: p.unit_price ?? line.unit_price })}
                  />
                  <input
                    className="mt-1 w-full text-xs text-muted-foreground placeholder:text-muted-foreground/40 bg-transparent border-0 outline-none"
                    placeholder="Satır açıklaması (opsiyonel)"
                    value={line.description}
                    onChange={e => setLine(idx, { description: e.target.value })}
                    disabled={readonly}
                  />
                </div>

                <input type="number" min="0" step="1"
                  className="h-9 w-full text-right text-sm rounded-md border border-input bg-background px-2 focus:outline-none focus:ring-2 focus:ring-ring"
                  value={line.quantity}
                  onChange={e => setLine(idx, { quantity: parseFloat(e.target.value) || 0 })}
                  disabled={readonly}
                />

                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">₺</span>
                  <input type="number" min="0" step="0.01"
                    className="h-9 w-full text-right text-sm rounded-md border border-input bg-background pl-6 pr-2 focus:outline-none focus:ring-2 focus:ring-ring"
                    value={line.unit_price}
                    onChange={e => setLine(idx, { unit_price: parseFloat(e.target.value) || 0 })}
                    disabled={readonly}
                  />
                </div>

                <select
                  className="h-9 w-full text-sm rounded-md border border-input bg-background px-2 focus:outline-none focus:ring-2 focus:ring-ring"
                  value={line.vat_rate}
                  onChange={e => setLine(idx, { vat_rate: parseFloat(e.target.value) })}
                  disabled={readonly}
                >
                  {VAT_OPTIONS.map(v => <option key={v} value={v}>%{v}</option>)}
                </select>

                <div className="h-9 flex items-center justify-end">
                  <span className="text-sm font-semibold text-foreground tabular-nums">
                    {formatCurrency(line.line_total, form.currency)}
                  </span>
                </div>

                <div className="h-9 flex items-center justify-center">
                  {!readonly && form.lines.length > 1 && (
                    <button type="button" onClick={() => removeLine(idx)}
                      className="text-muted-foreground/40 hover:text-destructive transition-colors p-1 rounded">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="px-5 py-4 border-t border-border flex justify-end">
            <div className="w-64 space-y-2 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Ara Toplam</span>
                <span className="tabular-nums">{formatCurrency(totals.subtotal, form.currency)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Toplam KDV</span>
                <span className="tabular-nums">{formatCurrency(totals.vat_amount, form.currency)}</span>
              </div>
              <div className="flex justify-between font-bold text-base border-t border-border pt-2 mt-1">
                <span>Genel Toplam</span>
                <span className="text-primary tabular-nums">{formatCurrency(totals.total, form.currency)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tahsilat Özeti & Geçmişi */}
      {savedId && (paidAmount > 0 || payments.length > 0) && (
        <Card className="surface-card-soft">
          <CardContent className="pt-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Tahsilat</p>
              {remaining > 0 && !readonly && (
                <button onClick={() => setShowPay(true)}
                  className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-800 font-medium transition-colors">
                  <Plus className="h-3.5 w-3.5" /> Ödeme Ekle
                </button>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg bg-muted/40 px-4 py-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">Fatura Tutarı</div>
                <div className="font-semibold tabular-nums">{formatCurrency(invoiceTotal, form.currency)}</div>
              </div>
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 text-center">
                <div className="text-xs text-emerald-700 mb-1">Tahsil Edilen</div>
                <div className="font-semibold text-emerald-800 tabular-nums">{formatCurrency(paidAmount, form.currency)}</div>
              </div>
              <div className={cn("rounded-lg px-4 py-3 text-center", remaining > 0 ? "bg-rose-50 border border-rose-100" : "bg-muted/40")}>
                <div className={cn("text-xs mb-1", remaining > 0 ? "text-rose-700" : "text-muted-foreground")}>Kalan</div>
                <div className={cn("font-semibold tabular-nums", remaining > 0 ? "text-rose-800" : "text-foreground")}>{formatCurrency(remaining, form.currency)}</div>
              </div>
            </div>

            {payments.length > 0 && (
              <div className="border border-border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30">
                    <tr className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                      <th className="px-4 py-2.5 text-left">Tarih</th>
                      <th className="px-4 py-2.5 text-left">Yöntem</th>
                      <th className="px-4 py-2.5 text-left">Not</th>
                      <th className="px-4 py-2.5 text-right">Tutar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {payments.map((p, i) => (
                      <tr key={p.id || i} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 text-muted-foreground">{fmtDate(p.date)}</td>
                        <td className="px-4 py-3">{PAYMENT_METHODS_LIST.find(m=>m.v===p.method)?.label || p.method}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">{p.notes || "—"}</td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums text-emerald-700">+{formatCurrency(p.amount, form.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Ödeme Modalı */}
      {showPay && savedId && (
        <PaymentModal
          invoiceId={savedId}
          remaining={remaining}
          onClose={() => setShowPay(false)}
          onSuccess={() => { setShowPay(false); refreshPayments(); }}
        />
      )}

      {/* Alt butonlar */}
      {!readonly && (
        <div className="flex justify-end gap-3 pb-6">
          <Button variant="outline" onClick={handleSave} disabled={saving || resmi} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Taslak Olarak Kaydet
          </Button>
          {savedId && (
            <Button
              onClick={handleResmilestir}
              disabled={saving || resmi}
              className="gap-2 bg-[#1487a3] hover:bg-[#117690] text-white"
            >
              {resmi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Resmileştir
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Liste Sayfası
// ══════════════════════════════════════════════════════════════════════════════

const STATUS_TABS = [
  { v: "",           label: "Tümü"       },
  { v: "taslak",     label: "Taslak"     },
  { v: "gonderildi", label: "Gönderildi" },
  { v: "odendi",     label: "Ödendi"     },
  { v: "gecikti",    label: "Gecikti"    },
  { v: "resmilesti", label: "Resmileşti" },
  { v: "iptal",      label: "İptal"      },
];

export default function ObiHubInvoicesPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [statusFilter, setStatus]   = useState("");
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const [actionId, setActionId]     = useState(null);
  const [summary, setSummary]       = useState(null);

  const loadSummary = useCallback(async () => {
    try { setSummary(await api.get("/local-invoices/summary")); } catch { /* swallow */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, per_page: 25 });
      if (search.trim()) p.set("search", search.trim());
      if (statusFilter)  p.set("status", statusFilter);
      const r = await api.get(`/local-invoices?${p}`);
      setInvoices(r.items || []);
      setTotal(r.total || 0);
      setTotalPages(r.total_pages || 1);
    } catch { toast.error("Faturalar yüklenemedi"); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const handleResmilestir = async (inv, e) => {
    e.stopPropagation();
    setActionId(inv.id);
    try {
      await api.post(`/local-invoices/${inv.id}/resmilestir`);
      toast.success("Fatura resmileştirildi");
      load();
    } catch (err) { toast.error(err?.message || "Başarısız"); }
    finally { setActionId(null); }
  };

  const handleDelete = async (inv, e) => {
    e.stopPropagation();
    if (!window.confirm(`"${inv.invoice_number}" silinsin mi?`)) return;
    try { await api.delete(`/local-invoices/${inv.id}`); toast.success("Silindi"); load(); }
    catch { toast.error("Silinemedi"); }
  };

  return (
    <div className="page-shell space-y-5">

      <div className="page-header">
        <div className="app-page-header">
          <h1 className="app-page-title">Faturalar</h1>
          <p className="app-page-subtitle">
            {!loading && total > 0 ? `${total} fatura` : "ObiHub'da oluşturulan faturalar"}
          </p>
        </div>
        <div className="page-header-actions">
          <Button
            className="gap-2 bg-[#1487a3] hover:bg-[#117690] text-white"
            onClick={() => navigate("/panel/satislar/fatura-olustur/yeni")}
          >
            <Plus className="h-4 w-4" />
            Yeni Fatura
          </Button>
        </div>
      </div>

      {/* Özet Kartlar */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="app-stat-card p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <TrendingUp className="h-3.5 w-3.5 text-primary" />
              Toplam Alacak
            </div>
            <div className="app-stat-value text-[22px]">{formatCurrency(summary.total_remaining)}</div>
            <div className="text-xs text-muted-foreground mt-1">{formatCurrency(summary.total_invoiced)} fatura</div>
          </div>
          <div className="app-stat-card p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <Banknote className="h-3.5 w-3.5 text-emerald-600" />
              Bu Ay Tahsilat
            </div>
            <div className="app-stat-value text-[22px] text-emerald-700">{formatCurrency(summary.collected_month)}</div>
            <div className="text-xs text-muted-foreground mt-1">Toplam: {formatCurrency(summary.total_paid)}</div>
          </div>
          <div className="app-stat-card p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <AlertCircle className="h-3.5 w-3.5 text-rose-600" />
              Geciken
            </div>
            <div className="app-stat-value text-[22px] text-rose-700">{formatCurrency(summary.overdue_amount)}</div>
            <div className="text-xs text-muted-foreground mt-1">{summary.overdue_count} fatura</div>
          </div>
          <div className="app-stat-card p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <Clock3 className="h-3.5 w-3.5 text-blue-600" />
              Bekleyen
            </div>
            <div className="app-stat-value text-[22px] text-blue-700">{formatCurrency(summary.pending_amount)}</div>
            <div className="text-xs text-muted-foreground mt-1">{summary.pending_count} fatura</div>
          </div>
        </div>
      )}

      <Card className="surface-card-soft">
        <CardContent className="pt-4 pb-3 space-y-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Fatura no veya müşteri ara…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 h-9 text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {STATUS_TABS.map(({ v, label }) => (
              <button key={v} onClick={() => { setStatus(v); setPage(1); }}
                className={cn(
                  "px-3 py-1 text-xs rounded-full font-medium border transition-colors",
                  statusFilter === v
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-input hover:border-foreground/40 hover:text-foreground"
                )}
              >{label}</button>
            ))}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <ReceiptText className="h-12 w-12 text-muted-foreground/25 mb-4" />
          <p className="text-base font-semibold text-muted-foreground">Fatura bulunamadı</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Yeni Fatura butonuna tıklayın</p>
        </div>
      ) : (
        <div className="table-shell overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                <th className="px-5 py-3 text-left">Fatura No</th>
                <th className="px-4 py-3 text-left">Müşteri</th>
                <th className="px-4 py-3 text-left">Tarih</th>
                <th className="px-4 py-3 text-left">Vade</th>
                <th className="px-4 py-3 text-right">Tutar</th>
                <th className="px-4 py-3 text-center">Durum</th>
                <th className="px-4 py-3 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoices.map(inv => (
                <tr key={inv.id}
                  className="hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => navigate(`/panel/satislar/fatura-olustur/${inv.id}`)}>
                  <td className="px-5 py-3.5">
                    <span className="font-mono text-xs font-semibold bg-muted px-2 py-0.5 rounded">
                      {inv.invoice_number}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="font-medium text-foreground">{inv.customer_name || "—"}</span>
                    {inv.customer_tax_no && (
                      <div className="text-xs text-muted-foreground mt-0.5">VKN: {inv.customer_tax_no}</div>
                    )}
                  </td>
                  <td className="px-4 py-3.5 text-muted-foreground">{fmtDate(inv.issue_date)}</td>
                  <td className="px-4 py-3.5 text-muted-foreground">{fmtDate(inv.due_date)}</td>
                  <td className="px-4 py-3.5 text-right">
                    <span className="font-semibold text-foreground tabular-nums">
                      {formatCurrency(inv.total, inv.currency)}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-center"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5">
                      {inv.status !== "resmilesti" && inv.status !== "iptal" && (
                        <button
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-colors disabled:opacity-50"
                          onClick={e => handleResmilestir(inv, e)}
                          disabled={actionId === inv.id}
                        >
                          {actionId === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                          Resmileştir
                        </button>
                      )}
                      {inv.status === "taslak" && (
                        <button
                          className="p-1.5 rounded text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors"
                          onClick={e => handleDelete(inv, e)}
                          title="Sil"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-border">
              <span className="text-xs text-muted-foreground">{total} fatura</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-1.5 rounded hover:bg-muted disabled:opacity-30 transition-colors">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-xs text-muted-foreground tabular-nums">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="p-1.5 rounded hover:bg-muted disabled:opacity-30 transition-colors">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
